import { useCallback } from 'react';

import { VFlex } from '@/components/base';
import SearchSelectModal from '@/components/Select/SearchSelectModal';
import type { TriggerPos } from '@/components/Select/types';
import { MainText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import { CUSTOMER_PAGE_SIZE } from '@/stores/CustomerStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { CONTACT_COLUMNS } from '@/types/filterKeyConst';
import { getPaginationQueries } from '@/utils/pagination';
import { buildQuery } from '@/utils/util';

type Props = {
  name: string;
  company?: string;
  company_info: CompanyInfoT | null;
  createNewCustomer?: (alias: string) => Promise<CustomerT>;
  toggleItem: (customer: PaginatedData<CustomerT> | null) => void;
  clearable: boolean;
  defaultQuery?: Record<string, string>;
  triggerEl?: HTMLDivElement | null;
  triggerPos?: TriggerPos;
};

const CustomerSelectModal = (props: Props) => {
  const color = useColor();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);

  const fetchData = useCallback(
    async (
      prev?: PaginatedData<CustomerT>[],
      currPage?: number,
      targetPage?: number,
      searchValue?: string,
      accumulate?: boolean,
      queryObj?: QueryObj,
    ) => {
      if (!currWorkspace?.alias) throw new Error('Workspace not found');

      const queries = getPaginationQueries<PaginatedData<CustomerT>>({
        queries: {
          contact_name: searchValue || null,
          ...queryObj,
          ...(props.defaultQuery || {}),
        },
        order: 'asc',
        prevData: prev || [],
        currPage: currPage || 0,
        targetPage: targetPage,
        sortby: 'name',
        cursorKey: 'paginate_token',
        acc: accumulate,
      });

      const queryString = buildQuery(queries);

      const res = await service.contact.fetchCustomerList(queryString);
      const hasNextPage = !!res[CUSTOMER_PAGE_SIZE - 1];

      return {
        list: res,
        page: targetPage || 0,
        hasNextPage,
      };
    },
    [currWorkspace?.alias, props.defaultQuery],
  );

  return (
    <SearchSelectModal
      name={props.name}
      triggerEl={props.triggerEl}
      triggerPos={props.triggerPos}
      placeholder="Search existing or add new..."
      createNewItem={props.createNewCustomer}
      toggleItem={props.toggleItem}
      dataList={[]}
      modalWidth="400px"
      async
      filter={
        props.company_info && props.company
          ? {
              queryKey: CONTACT_COLUMNS.ACCOUNT,
              queryValue: props.company,
              valueName: props.company_info.name,
            }
          : undefined
      }
      clearable={props.clearable}
      clearText={<MainText dim>Not Assigned</MainText>}
      onClear={() => props.toggleItem(null)}
      fetchFn={fetchData}
      parser={(customers) =>
        customers.map((customer) => ({
          alias: customer.alias,
          name: customer.name || '',
          node: (
            <VFlex style={{ wordBreak: 'keep-all' }} g={4}>
              {customer.company_info && (
                <Typo type="label4" color={color.gray600}>
                  {customer.company_info.name}
                </Typo>
              )}
              <VFlex g={2}>
                <MainText weight="b">{customer.name}</MainText>
                {customer.email && customer.email.length > 0 && (
                  <MainText dim>{customer.email}</MainText>
                )}
              </VFlex>
            </VFlex>
          ),
        }))
      }
    />
  );
};

export default CustomerSelectModal;
